import type { TranslationKey } from "@privacy/translations";
import { useState, type FC, type ReactNode } from "react";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import { useMutation } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useTranslation } from "@privacy/translations";
import Typography from "@mui/material/Typography";
import { useFormContext, useController } from "react-hook-form";
import { useDynamicFormState } from "../../DynamicFormProvider";
import { EmptyPlaceholderText } from "../EmptyPlaceholderText";
import type { InputProps } from "../../DynamicInput";
import { CircleXIcon, XIcon, YesIcon } from "../../../shared/icons";
import type { AnswerRequest, YesOrNoInput } from "../../types";
import { ErrorMessage } from "../ErrorMessage";
import { isAnswerRequestEmpty } from "../../shared/isAnswerRequestEmpty";
import { inlineInputStateStyles, menuProps, useInlineInputState } from "./InlineInputState";
import { HACK_alignIconWithLabel } from "@privacy/ui";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import type { Theme } from "@mui/material/styles";

export type YesOrNoValue = Extract<AnswerRequest, { type: YesOrNoInput["type"] }>["value"];
export type YesOrNoWithDescriptionProps = InputProps<YesOrNoValue> &
  Pick<YesOrNoInput, "yesDescription" | "noDescription">;
export const YesOrNo: FC<YesOrNoWithDescriptionProps> = ({
  questionCard,
  onSave,
  readonly,
  yesDescription,
  noDescription,
}) => {
  const { t } = useTranslation();
  const { control } = useFormContext();
  const { field } = useController({ control, name: questionCard.id });
  const { mandatoryQuestionErrorMessage } = useDynamicFormState();
  const { inputState, setIsEditing } = useInlineInputState(readonly);
  const saveMutation = useMutation({
    mutationFn: (newValue: YesOrNoValue) => {
      setIsEditing(false);
      field.onChange(newValue);
      return onSave(newValue);
    },
  });

  const [isHovering, setIsHovering] = useState(false);
  const showClearButton = !readonly && field.value && (inputState === "editing" || isHovering);

  return (
    <FormControl fullWidth>
      <Select<YesOrNoValue>
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        disabled={readonly}
        size="small"
        sx={inlineInputStateStyles[inputState]}
        onFocus={() => setIsEditing(true)}
        renderValue={selectedValue => (
          <YesOrNoInputSelectedValue value={selectedValue} emptyPlaceHolder={<EmptyPlaceholderText />} />
        )}
        endAdornment={
          /** https://github.com/mui/material-ui/issues/27141 */
          showClearButton && (
            <InputAdornment sx={{ mr: 4 }} position="end">
              <IconButton
                aria-label={t("DynamicForm.Inline.YesOrNoClearButton")}
                size="small"
                onClick={() => saveMutation.mutate(null)}
              >
                <XIcon size={14} color={(theme: Theme) => theme.palette.text.primary} />
              </IconButton>
            </InputAdornment>
          )
        }
        displayEmpty
        onBlur={() => setIsEditing(false)}
        onClose={() => setIsEditing(false)}
        value={field.value ?? ""}
        inputRef={field.ref}
        MenuProps={menuProps}
        onChange={e => {
          const newValue = e.target.value;
          if (!isYesOrNoValue(newValue)) {
            throw new Error(`Invalid value was provided "${newValue}" for questionId "${questionCard.id}"`);
          }
          saveMutation.mutate(newValue);
        }}
        inputProps={{ "aria-label": questionCard.id, "id": questionCard.id }}
      >
        <MenuItem sx={{ display: "none" }} value="" />

        <MenuItem sx={{ height: "auto" }} value="yes">
          <Stack>
            <Typography variant="body2" sx={{ fontWeight: yesDescription ? 600 : "revert" }}>
              {t(yesOrNoInfo.yes.labelKey)}
            </Typography>
            {yesDescription && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: theme => theme.typography.pxToRem(12),
                  color: theme => theme.palette.text.tertiary,
                  textWrap: "wrap",
                }}
              >
                {yesDescription}
              </Typography>
            )}
          </Stack>
        </MenuItem>
        <MenuItem value="no">
          <Stack>
            <Typography variant="body2" sx={{ fontWeight: noDescription ? 600 : "revert" }}>
              {t(yesOrNoInfo.no.labelKey)}
            </Typography>
            {noDescription && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: theme => theme.typography.pxToRem(12),
                  color: theme => theme.palette.text.tertiary,
                  textWrap: "balance",
                }}
              >
                {noDescription}
              </Typography>
            )}
          </Stack>
        </MenuItem>
      </Select>
      {!saveMutation.isPending && saveMutation.isError && <ErrorMessage />}
      {mandatoryQuestionErrorMessage && questionCard.mandatory && isAnswerRequestEmpty(field.value) && (
        <ErrorMessage variant="warning" errorMessage={mandatoryQuestionErrorMessage} />
      )}
    </FormControl>
  );
};

export const yesOrNoInfo: Record<"yes" | "no", { labelKey: TranslationKey; icon: ReactNode }> = {
  yes: {
    labelKey: "DynamicForm.YesOrNoInput.YesLabel",
    icon: <YesIcon color={theme => theme.palette.primary.main} size={16} />,
  },
  no: { labelKey: "DynamicForm.YesOrNoInput.NoLabel", icon: <CircleXIcon size={16} /> },
};

export const isYesOrNoValue = (value: unknown): value is YesOrNoValue =>
  value === "yes" || value === "no" || value === null;

export const YesOrNoInputSelectedValue = ({
  value,
  emptyPlaceHolder = null,
}: {
  value: YesOrNoValue | null;
  emptyPlaceHolder?: ReactNode;
}) => {
  const { t } = useTranslation();

  if (!isYesOrNoValue(value) || value === null) return emptyPlaceHolder;

  const { labelKey, icon } = yesOrNoInfo[value];
  return (
    <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
      {icon}
      <Box sx={{ ...HACK_alignIconWithLabel, fontSize: theme => theme.typography.pxToRem(14) }}>{t(labelKey)}</Box>
    </Stack>
  );
};
